import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { formatDateNumber } from '@seen/core/dates'
import { formatObservationValueDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { TypographyPageTitle } from '@seen/ui'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import { type JSX, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { LinkCell } from '#src/components/core/data-table/LinkCell.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

// Every observation for the participant, one row each, newest first until a
// column is sorted. With facesheet-improvements off the route renders nothing,
// as it did before this page existed.
export function ParticipantObservationsPage(): JSX.Element | null {
  const { patientId } = useParams()
  invariant(patientId, 'Patient ID is required')
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  if (!facesheetImprovements) {
    return null
  }
  return (
    <ParticipantObservationsPageComponent
      key={patientId}
      patientId={patientId}
    />
  )
}

interface ObservationRow {
  observation: ExtendedObservation
  name: string
  value: string
  // The first quantity, so "6.4%" sorts before "10 lbs" and "6.10%".
  numericValue?: number
  date?: Date
  // Also the sort key for practitioner sources, from their saved display.
  source?: string
  sourcePractitioner?: Reference<Practitioner>
}

function ParticipantObservationsPageComponent({
  patientId,
}: {
  patientId: string
}): JSX.Element {
  // Pages through every result: some participants have well over the
  // 1,000-per-request limit.
  const { data: observations, isLoading } = useSearchResourcePages(
    ExtendedObservation,
    {
      ...ExtendedObservation.participantObservationsQuery(
        `Patient/${patientId}`
      ),
      // _id breaks ties, or rows with the same date can move between pages
      // and be skipped.
      _sort: '-date,_id',
      _count: 1000,
    }
  )

  const rows = useMemo(
    () =>
      (observations ?? []).map((observation): ObservationRow => {
        const resource = observation.toFhirResource()
        const dateString = observation.effectiveDateTime ?? observation.issued
        return {
          observation,
          name: observation.display ?? observation.code ?? 'Observation',
          value: formatObservationValueDisplay(resource),
          numericValue:
            resource.valueQuantity?.value ??
            resource.component?.[0]?.valueQuantity?.value,
          // A date-only value stays on its calendar day in every timezone.
          date: dateString ? parseISO(dateString) : undefined,
          source: observation.sourceLabel,
          sourcePractitioner: observation.sourcePractitioner,
        }
      }),
    [observations]
  )

  const [sorting, setSorting] = useState<SortingState>([])

  return (
    <div className="flex flex-col gap-6">
      <TypographyPageTitle>Observations</TypographyPageTitle>
      {isLoading && <Loader />}
      {observations && (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.observation.id}
          enableSorting
          onSortingChange={setSorting}
          state={{ sorting }}
          // Virtualizing scrolls the table in its own 70vh box, which past the
          // default 100 rows gave some participants a nested scroll. The page
          // scrolls instead, at any row count.
          virtualizeThreshold={Number.POSITIVE_INFINITY}
        />
      )}
    </div>
  )
}

function linkTo(row: ObservationRow): string {
  return `./${row.observation.id}`
}

const columns: ColumnDef<ObservationRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    sortingFn: 'text',
    cell: ({ row }) => (
      <LinkCell to={linkTo(row.original)}>{row.original.name}</LinkCell>
    ),
  },
  {
    accessorKey: 'value',
    header: 'Value',
    // Numbers in numeric order, then values with no quantity as text.
    sortingFn: (a, b) => {
      const x = a.original.numericValue
      const y = b.original.numericValue
      if (x !== undefined && y !== undefined && x !== y) {
        return x < y ? -1 : 1
      }
      if (x !== undefined && y === undefined) {
        return -1
      }
      if (x === undefined && y !== undefined) {
        return 1
      }
      return a.original.value.localeCompare(b.original.value)
    },
    cell: ({ row }) => (
      <LinkCell to={linkTo(row.original)}>{row.original.value}</LinkCell>
    ),
  },
  {
    accessorKey: 'date',
    header: 'Date',
    sortingFn: 'datetime',
    sortUndefined: 'last',
    cell: ({ row }) =>
      row.original.date ? formatDateNumber(row.original.date) : null,
  },
  {
    accessorKey: 'source',
    header: 'Source',
    sortingFn: 'text',
    sortUndefined: 'last',
    cell: ({ row }) =>
      row.original.sourcePractitioner ? (
        <PractitionerDisplay practitioner={row.original.sourcePractitioner} />
      ) : (
        row.original.source
      ),
  },
]
